import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Icon
} from '@chakra-ui/react'
import { transparentize } from '@chakra-ui/theme-tools'
import { Permission } from '@le2/common-types'
import { merge } from 'lodash'
import { useMemo } from 'react'
import { NavLink, useHistory } from 'react-router-dom'

import PermissionWrapper from '../../components/PermissionWrapper'
import { useSideNav } from './sideNavContext'

export interface MenuItemWithSubitemsProps {
  icon: any
  label: string
  permission?: Permission
  subitems: MenuSubitemProps[]
}

export interface MenuSingleItemProps {
  icon: any
  label: string
  to: string
  permission?: Permission
}

export interface MenuSubitemProps {
  label: string
  to: string
  permission?: Permission
}

export type MenuItemProps = MenuSingleItemProps | MenuItemWithSubitemsProps

interface MenuItemContainerProps {
  children: React.ReactElement | React.ReactElement[]
  isSubitem: boolean
  to: string
  mb?: string
  isActive: boolean
}

const accordionTransitions = 'height 0.3s'
const activeLinkBgColor = transparentize('secondary', 0.1)
const hoverLinkBgColor = transparentize('secondary', 0.2)
const panelPaddingTop = 8
const panelItemMarginBotton = 4
const panelItemHeight = 56 + panelItemMarginBotton

export function MenuItem({ ...props }: MenuItemProps) {
  const {
    location: { pathname, search }
  } = useHistory()
  const completeUrl = pathname + search

  if ((props as MenuItemWithSubitemsProps).subitems) {
    const itemProps = props as MenuItemWithSubitemsProps
    const subitemActive = itemProps.subitems.find((subitem) => completeUrl.indexOf(subitem.to) !== -1)
    return !itemProps.subitems.length ? null : <MenuSubitemContainer {...itemProps} subitemActive={subitemActive} />
  } else {
    const isActive = completeUrl.indexOf((props as MenuSingleItemProps).to) !== -1
    return <SingleMenuItem {...(props as MenuSingleItemProps)} isActive={isActive} />
  }
}

function SingleMenuItem(props: MenuSingleItemProps & { isActive: boolean }) {
  const { isSideNavOpen } = useSideNav()
  return (
    <MenuItemContainer
      data-cy={props.label.replace(/ /g, '')}
      to={props.to}
      isSubitem={false}
      isActive={props.isActive}
    >
      <Icon as={props.icon} w={6} h={6} />
      <Box ml={2} fontWeight={'bold'} display={{ base: 'inline-block', xl: isSideNavOpen ? 'inline-block' : 'none' }}>
        {props.label}
      </Box>
    </MenuItemContainer>
  )
}

function MenuSubitemContainer(props: MenuItemWithSubitemsProps & { subitemActive?: MenuSubitemProps }) {
  const { isSideNavOpen } = useSideNav()
  const panelHeight = `${panelItemHeight * props.subitems.length + panelPaddingTop}px`

  const subitemsPermissions = useMemo(() => {
    return props.subitems.reduce((subitems, subitem) => {
      if (subitem.permission) {
        subitems.push(subitem.permission)
      }

      return subitems
    }, [] as Permission[])
  }, [props.subitems])

  return (
    // Verifies if satisfies as minimum one permission to display the menu item.
    <PermissionWrapper permission={merge(subitemsPermissions, [props.permission])} strict={false}>
      <Accordion allowMultiple>
        <AccordionItem borderColor="transparent" boxShadow="none" isFocusable={false}>
          {({ isExpanded }) => (
            <>
              <AccordionButton
                data-cy={props.label.replace(/ /g, '')}
                color={
                  isSideNavOpen
                    ? !isExpanded && props.subitemActive
                      ? 'secondary'
                      : 'gray.200'
                    : props.subitemActive
                    ? 'secondary'
                    : 'gray.200'
                }
                px={3}
                py={4}
                borderRadius={8}
                _focus={{
                  boxShadow: 'none'
                }}
                _hover={{
                  // @ts-ignore
                  background: hoverLinkBgColor
                }}
                // @ts-ignore
                backgroundColor={
                  isSideNavOpen
                    ? !isExpanded && props.subitemActive && activeLinkBgColor
                    : props.subitemActive && activeLinkBgColor
                }
              >
                <Box flex="1" textAlign="left">
                  <Icon as={props.icon} w={6} h={6} pl={0} />
                  <Box
                    ml={2}
                    fontWeight={'bold'}
                    display={{ base: 'inline-block', xl: isSideNavOpen ? 'inline-block' : 'none' }}
                  >
                    {props.label}
                  </Box>
                </Box>
                {isSideNavOpen && (
                  <AccordionIcon h={6} color={!isExpanded && props.subitemActive ? 'secondary' : 'gray.200'} />
                )}
              </AccordionButton>
              <AccordionPanel
                pt={isSideNavOpen ? `${panelPaddingTop}px` : 0}
                px={0}
                pb={0}
                height={{ xl: isSideNavOpen ? panelHeight : 0 }}
                overflow="hidden"
                transition={{ xl: accordionTransitions }}
              >
                {props.subitems.map((subitem, index) => (
                  // Verifies the subitems permissions.
                  <PermissionWrapper key={index} permission={subitem.permission}>
                    <MenuSubitem subitem={subitem} isSideNavOpen={isSideNavOpen} subitemActive={props.subitemActive} />
                  </PermissionWrapper>
                ))}
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>
    </PermissionWrapper>
  )
}

interface CMenuSubitemProps {
  subitem: MenuSubitemProps
  subitemActive: MenuSubitemProps | undefined
  isSideNavOpen: boolean
}
function MenuSubitem({ subitem, subitemActive, isSideNavOpen }: CMenuSubitemProps) {
  const history = useHistory()

  return (
    <MenuItemContainer
      data-cy={subitem.label.replace(/ /g, '')}
      to={subitem.to}
      mb={`${panelItemMarginBotton}px`}
      isSubitem={true}
      isActive={
        history.location.pathname.startsWith('/bookings/new')
          ? history.location.pathname.startsWith('/bookings/new') === subitem.to.startsWith('/bookings/new') &&
            subitem.to.split('?')[1] === history.location.search.replace('?', '')
          : subitemActive === subitem
      }
    >
      <Box ml={8} fontWeight={'bold'} display={{ base: 'inline-block', xl: isSideNavOpen ? 'inline-block' : 'none' }}>
        {subitem.label}
      </Box>
    </MenuItemContainer>
  )
}

function MenuItemContainer({ isSubitem, isActive, ...props }: MenuItemContainerProps) {
  const { togglePinSideNav, isSideNavOpen, isScreenLargerThanXL } = useSideNav()

  return (
    <Flex
      as={NavLink}
      onClick={() => !isScreenLargerThanXL && togglePinSideNav()}
      px={3}
      py={4}
      borderRadius={8}
      // @ts-ignore
      background={isActive && (!isSubitem || isSideNavOpen) ? activeLinkBgColor : 'none'}
      color={isActive && (!isSubitem || isSideNavOpen) ? 'secondary' : 'gray.200'}
      _hover={{
        // @ts-ignore
        background: hoverLinkBgColor
      }}
      {...props}
    />
  )
}
